/**
 * Sprint B / ADR-019 — Sous-app frontend invité externe.
 *
 * Strictement isolée du store M24 :
 *  - Pas de useAuthStore (M24)
 *  - Pas de Layout/AppHeader
 *  - Pas de PdfEditorPage normal (juste un viewer minimaliste pour V1)
 *  - Token stocké dans sessionStorage (pas localStorage : disparaît à
 *    la fermeture de l'onglet, force re-auth)
 *
 * 3 vues selon le path :
 *  - /external/accept?token=...  : charte + créer compte + setup MFA
 *  - /external/login             : email + pwd → MFA verify → dossier
 *  - /external/dossier           : viewer minimaliste, onglets selon
 *                                  permissions
 */

import { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

const EXTERNAL_TOKEN_KEY = 'adr_external_access_token';

function getExternalToken(): string | null {
  return sessionStorage.getItem(EXTERNAL_TOKEN_KEY);
}
function setExternalToken(t: string | null) {
  if (t) sessionStorage.setItem(EXTERNAL_TOKEN_KEY, t);
  else sessionStorage.removeItem(EXTERNAL_TOKEN_KEY);
}

async function externalApi<T>(path: string, init?: RequestInit & { auth?: boolean }): Promise<T> {
  const headers = new Headers(init?.headers || {});
  if (init?.auth !== false) {
    const token = getExternalToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const resp = await fetch(`/api/v1/external${path}`, { ...init, headers });
  const text = await resp.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { raw: text };
    }
  }
  if (!resp.ok) {
    const detail =
      (parsed as { detail?: string; title?: string })?.detail ||
      (parsed as { title?: string })?.title ||
      `HTTP ${resp.status}`;
    throw new Error(detail);
  }
  return parsed as T;
}

export default function ExternalApp() {
  return (
    <div className="min-h-screen bg-paper text-text-primary">
      <ExternalBanner />
      <Routes>
        <Route path="accept" element={<ExternalAcceptPage />} />
        <Route path="login" element={<ExternalLoginPage />} />
        <Route path="dossier" element={<ExternalDossierPage />} />
        <Route path="*" element={<Navigate to="login" replace />} />
      </Routes>
    </div>
  );
}

function ExternalBanner() {
  return (
    <header className="border-b-2 border-cat-imp/40 bg-cat-imp-pastel px-4 py-2 text-center text-[12px] text-cat-imp-text">
      Vous accédez à un dossier confidentiel partagé par M24 Avocats. Tout accès est tracé. Le
      contenu est protégé par le secret professionnel (art. 458 CP).
    </header>
  );
}

// ---------------------------------------------------------------------------
// Étape 1 : Accept (charte + création compte)
// ---------------------------------------------------------------------------

type InvitationInfo = {
  invitation_id: string;
  invited_email: string;
  invited_display_name: string;
  invitation_type: string;
  expires_at: string;
  inviter_display_name: string | null;
  dossier_label: string | null;
  charter: { version: string; content_markdown: string };
};

function ExternalAcceptPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    external_user_id: string;
    mfa_provisioning_uri: string;
  } | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Token manquant — vérifie le lien reçu par email.');
      return;
    }
    externalApi<InvitationInfo>(`/invitations/${token}`, { auth: false })
      .then(setInfo)
      .catch((e) => setError((e as Error).message));
  }, [token]);

  const submit = async () => {
    if (password.length < 12) {
      setError('Mot de passe minimum 12 caractères');
      return;
    }
    if (password !== confirmPwd) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (!accepted) {
      setError('Tu dois accepter la charte de confidentialité');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await externalApi<{
        external_user_id: string;
        mfa_provisioning_uri: string;
      }>(`/invitations/${token}/accept`, {
        method: 'POST',
        auth: false,
        body: JSON.stringify({
          password,
          charter_version: info!.charter.version,
          charter_accepted: true,
        }),
      });
      setResult(res);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !info) {
    return (
      <div className="mx-auto mt-12 max-w-md rounded-md border border-cat-def/40 bg-cat-def-pastel p-6">
        <p className="font-medium text-cat-def">{error}</p>
      </div>
    );
  }
  if (!info) {
    return <p className="p-8 text-center">Chargement…</p>;
  }
  if (result) {
    return (
      <div className="mx-auto mt-8 max-w-2xl space-y-4 rounded-md border border-rule bg-paper-elevated p-6">
        <h1 className="font-serif text-[20px]">Compte créé — étape MFA</h1>
        <p className="text-[13px] text-text-secondary">
          Ouvre ton application TOTP (Google Authenticator, Authy, 1Password, Bitwarden…) et scanne
          ce QR code, ou entre le secret manuellement.
        </p>
        <div className="rounded-md border border-rule bg-paper-card p-3">
          <code className="block break-all font-mono text-[10px]">
            {result.mfa_provisioning_uri}
          </code>
          <p className="mt-2 text-[11px] text-text-tertiary">
            Une fois ajouté à ton app TOTP, va sur{' '}
            <Link to="/external/login" className="text-accent-text underline">
              /external/login
            </Link>{' '}
            avec ton email + mot de passe + le code à 6 chiffres généré par l'app.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-6 max-w-2xl space-y-5 rounded-md border border-rule bg-paper-elevated p-6">
      <header>
        <h1 className="font-serif text-[20px] text-text-primary">
          Invitation au dossier {info.dossier_label}
        </h1>
        <p className="text-[12px] text-text-tertiary">
          Invité par {info.inviter_display_name} · expire le{' '}
          {new Date(info.expires_at).toLocaleDateString('fr-BE')}
        </p>
      </header>

      <section className="max-h-64 overflow-y-auto rounded-md border border-rule bg-paper-card p-3 text-[12px] leading-[1.5] whitespace-pre-wrap">
        {info.charter.content_markdown}
      </section>

      <div className="space-y-2">
        <label className="block text-[12px]">
          <span className="text-text-secondary">Mot de passe (min 12)</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-2 text-[13px]"
          />
        </label>
        <label className="block text-[12px]">
          <span className="text-text-secondary">Confirmation</span>
          <input
            type="password"
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-2 text-[13px]"
          />
        </label>
        <label className="flex items-start gap-2 text-[12px]">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1"
          />
          <span>
            J'ai lu et j'accepte la charte de confidentialité v{info.charter.version}
            ci-dessus, ainsi que les engagements qui en découlent.
          </span>
        </label>
      </div>

      {error && <p className="text-[12px] text-cat-def">{error}</p>}

      <button
        type="button"
        disabled={submitting}
        onClick={() => void submit()}
        className="w-full rounded-md bg-accent px-4 py-2 text-[13px] font-medium text-paper-elevated hover:bg-accent/90 disabled:opacity-50"
      >
        {submitting ? 'Création…' : 'Créer mon compte'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Étape 2 : Login + MFA verify
// ---------------------------------------------------------------------------

function ExternalLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'pwd' | 'mfa'>('pwd');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submitPwd = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await externalApi<{ mfa_pending_token: string }>('/auth/login', {
        method: 'POST',
        auth: false,
        body: JSON.stringify({ email, password }),
      });
      setPendingToken(res.mfa_pending_token);
      setStep('mfa');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitMfa = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await externalApi<{ access_token: string }>('/auth/mfa/verify', {
        method: 'POST',
        auth: false,
        body: JSON.stringify({ mfa_pending_token: pendingToken, code }),
      });
      setExternalToken(res.access_token);
      navigate('/external/dossier', { replace: true });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto mt-12 max-w-sm space-y-4 rounded-md border border-rule bg-paper-elevated p-6">
      <h1 className="font-serif text-[18px]">Accès dossier confidentiel</h1>
      {step === 'pwd' ? (
        <>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-rule-strong bg-paper-card px-3 py-2 text-[13px]"
          />
          <input
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-rule-strong bg-paper-card px-3 py-2 text-[13px]"
          />
          {error && <p className="text-[12px] text-cat-def">{error}</p>}
          <button
            type="button"
            disabled={submitting}
            onClick={() => void submitPwd()}
            className="w-full rounded-md bg-accent px-4 py-2 text-[13px] font-medium text-paper-elevated hover:bg-accent/90 disabled:opacity-50"
          >
            {submitting ? 'Vérification…' : 'Continuer'}
          </button>
        </>
      ) : (
        <>
          <p className="text-[12px] text-text-secondary">
            Code à 6 chiffres généré par ton app TOTP :
          </p>
          <input
            type="text"
            inputMode="numeric"
            pattern="\\d{6}"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="w-full rounded-md border border-rule-strong bg-paper-card px-3 py-2 text-center font-mono text-[18px]"
            maxLength={6}
          />
          {error && <p className="text-[12px] text-cat-def">{error}</p>}
          <button
            type="button"
            disabled={submitting || code.length !== 6}
            onClick={() => void submitMfa()}
            className="w-full rounded-md bg-accent px-4 py-2 text-[13px] font-medium text-paper-elevated hover:bg-accent/90 disabled:opacity-50"
          >
            {submitting ? 'Vérification…' : 'Se connecter'}
          </button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Étape 3 : Dossier (viewer minimaliste selon permissions)
// ---------------------------------------------------------------------------

type ExternalDossierInfo = {
  dossier_id: string;
  ref_cabinet: string;
  titre: string | null;
  client_name: string | null;
  permissions: Record<string, boolean>;
  inviter_display_name: string | null;
};

function ExternalDossierPage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState<ExternalDossierInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfs, setPdfs] = useState<
    { id: string; filename_original: string; page_count: number | null }[]
  >([]);
  const [pieceClients, setPieceClients] = useState<{ id: string; titre: string }[]>([]);

  useEffect(() => {
    externalApi<ExternalDossierInfo>('/dossier')
      .then(async (d) => {
        setInfo(d);
        if (d.permissions.view_pieces) {
          const ps = await externalApi<typeof pdfs>('/dossier/pdfs').catch(() => []);
          setPdfs(ps);
        }
        if (d.permissions.view_pieces_client) {
          const pcs = await externalApi<typeof pieceClients>('/dossier/pieces-client').catch(
            () => []
          );
          setPieceClients(pcs);
        }
      })
      .catch((e) => {
        const msg = (e as Error).message;
        if (msg.includes('token') || msg.includes('invalid')) {
          setExternalToken(null);
          navigate('/external/login', { replace: true });
        } else {
          setError(msg);
        }
      });
  }, [navigate]);

  if (error) return <p className="p-8 text-cat-def">{error}</p>;
  if (!info) return <p className="p-8">Chargement…</p>;

  return (
    <div className="mx-auto mt-4 max-w-4xl space-y-4 px-4">
      <header className="rounded-md border border-rule bg-paper-elevated p-4">
        <h1 className="font-serif text-[20px]">
          Dossier {info.client_name || info.titre || info.ref_cabinet}
        </h1>
        <p className="text-[12px] text-text-tertiary">
          Référence cabinet : <span className="font-mono">{info.ref_cabinet}</span> · Invité par{' '}
          {info.inviter_display_name}
        </p>
      </header>

      {info.permissions.view_pieces && (
        <section className="rounded-md border border-rule bg-paper-elevated p-4">
          <h2 className="mb-2 font-serif text-[15px]">Pièces (PDF greffe)</h2>
          {pdfs.length === 0 ? (
            <p className="text-[12px] text-text-tertiary">Aucune pièce.</p>
          ) : (
            <ul className="space-y-1">
              {pdfs.map((p) => (
                <li key={p.id}>
                  <a
                    href={`/api/v1/external/dossier/pdfs/${p.id}/stream`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-text hover:underline"
                  >
                    {p.filename_original}
                  </a>
                  {p.page_count != null && (
                    <span className="ml-2 text-[11px] text-text-tertiary">
                      {p.page_count} pages
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {info.permissions.view_pieces_client && (
        <section className="rounded-md border border-rule bg-paper-elevated p-4">
          <h2 className="mb-2 font-serif text-[15px]">Pièces client</h2>
          {pieceClients.length === 0 ? (
            <p className="text-[12px] text-text-tertiary">Aucune pièce client.</p>
          ) : (
            <ul className="space-y-1">
              {pieceClients.map((p) => (
                <li key={p.id} className="text-[13px]">
                  {p.titre}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <p className="text-center text-[10px] text-text-tertiary">
        Sous-app externe ADR-M24 v1.0 · vos actions sont tracées dans le journal du cabinet.
      </p>
    </div>
  );
}
